import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { ResultCard } from "./result-card.js";
import { shareResultCard, type ShareFallback } from "./share-image.js";

/**
 * A button that renders a result card and opens the share sheet, with an
 * in-app dialog (image, save hint, download link) where sharing files
 * is not supported.
 */
export function ShareCardButton(props: {
  label: string;
  card(): ResultCard | Promise<ResultCard>;
  variant?: "gold" | "outline-gold" | "link";
  size?: "sm" | "block";
  className?: string;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [fallback, setFallback] = useState<ShareFallback | null>(null);
  const share = async () => {
    setBusy(true);
    try {
      const outcome = await shareResultCard(await props.card());
      if (outcome.kind === "fallback") setFallback(outcome.fallback);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Sharing did not work.",
      );
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (fallback) URL.revokeObjectURL(fallback.url);
    setFallback(null);
  };
  return (
    <>
      <button
        type="button"
        className={props.className ?? "fr-btn"}
        data-variant={props.variant ?? "outline-gold"}
        data-size={props.size}
        disabled={busy}
        onClick={() => void share()}
      >
        <ShareGlyph />
        {busy ? "Preparing image…" : props.label}
      </button>
      <ShareFallbackDialog fallback={fallback} onClose={close} />
    </>
  );
}

function ShareFallbackDialog(props: {
  fallback: ShareFallback | null;
  onClose(): void;
}): React.JSX.Element {
  const { fallback } = props;
  return (
    <Dialog
      open={Boolean(fallback)}
      onOpenChange={(open) => !open && props.onClose()}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="fr-dialog-veil"
        className="max-h-[92dvh] w-[min(100%-24px,410px)] max-w-none gap-0 overflow-y-auto rounded-2xl border border-gold/50 bg-bg-2 p-4 text-text shadow-2xl"
      >
        {fallback ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <DialogTitle className="fr-eyebrow">Share</DialogTitle>
              <button
                type="button"
                className="fr-x"
                aria-label="Close"
                onClick={props.onClose}
              >
                ×
              </button>
            </div>
            <img
              src={fallback.url}
              alt="Your Fairway Rivalry card"
              className="mx-auto block max-h-[60dvh] w-auto rounded-lg border border-gold/40"
            />
            <p className="mt-3 text-center text-sm text-text-2">
              Long-press the image to save it
            </p>
            <p className="mt-2 text-center text-xs text-muted-warm">
              {fallback.text}
            </p>
            <CopyResultText text={fallback.text} />
            <a
              className="fr-btn mt-4"
              data-variant="gold"
              data-size="block"
              href={fallback.url}
              download={fallback.fileName}
            >
              Download image
            </a>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function CopyResultText({ text }: { text: string }) {
  return (
    <button
      type="button"
      className="fr-btn mt-3"
      data-variant="outline-gold"
      data-size="block"
      onClick={() => {
        if (!navigator.clipboard) {
          toast.error("Select the result text above to copy it.");
          return;
        }
        void navigator.clipboard.writeText(text).then(
          () => toast.success("Result and scorecard link copied."),
          () => toast.error("Select the result text above to copy it."),
        );
      }}
    >
      Copy result & link
    </button>
  );
}

function ShareGlyph(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v7h14v-7" />
    </svg>
  );
}
